import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, Copy, ArrowLeft, CheckCircle, Loader2, Save, Play, Pause, Edit3, X, Clock } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '../context/AuthContext';
import BrandingFooter from '@/components/BrandingFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ResultsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [transcription, setTranscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subtitles, setSubtitles] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editText, setEditText] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentSubtitle, setCurrentSubtitle] = useState('');
  const [videoAvailable, setVideoAvailable] = useState(false);
  const videoRef = useRef(null);
  const subtitleListRef = useRef(null);
  const { getAuthHeaders, token } = useAuth();

  useEffect(() => {
    fetchTranscription();
  }, [id]);

  // Update current subtitle based on video time
  useEffect(() => {
    if (subtitles.length > 0) {
      const active = subtitles.find(sub => {
        const start = parseTimestamp(sub.startTime);
        const end = parseTimestamp(sub.endTime);
        return currentTime >= start && currentTime <= end;
      });
      setCurrentSubtitle(active ? active.text : '');
      
      // Auto-scroll to active subtitle
      if (active && subtitleListRef.current) {
        const activeElement = document.getElementById(`subtitle-${active.index}`);
        if (activeElement) {
          activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }
  }, [currentTime, subtitles]);

  const fetchTranscription = async () => {
    try {
      const response = await axios.get(`${API}/transcription/${id}`, {
        headers: getAuthHeaders()
      });
      setTranscription(response.data);
      const parsed = parseSRT(response.data.srt_content);
      setSubtitles(parsed);
      
      // Check if video is available
      if (response.data.video_path) {
        setVideoAvailable(true);
      }
    } catch (error) {
      console.error('Error fetching transcription:', error);
      toast.error('Failed to load transcription');
    } finally {
      setLoading(false);
    }
  };

  const parseSRT = (srtContent) => {
    if (!srtContent) return [];
    const blocks = srtContent.trim().split('\n\n');
    return blocks.map(block => {
      const lines = block.split('\n');
      if (lines.length >= 3) {
        const timestamps = lines[1].split(' --> ');
        return {
          index: parseInt(lines[0]),
          startTime: timestamps[0],
          endTime: timestamps[1],
          text: lines.slice(2).join('\n')
        };
      }
      return null;
    }).filter(Boolean);
  };

  const parseTimestamp = (timestamp) => {
    if (!timestamp) return 0;
    const parts = timestamp.replace(',', '.').split(':');
    if (parts.length === 3) {
      const hours = parseFloat(parts[0]);
      const minutes = parseFloat(parts[1]);
      const seconds = parseFloat(parts[2]);
      return hours * 3600 + minutes * 60 + seconds;
    }
    return 0;
  };

  const formatTimestampForEdit = (timestamp) => {
    return timestamp || '00:00:00,000';
  };

  const generateSRT = (subtitlesList) => {
    return subtitlesList.map((sub, idx) => {
      return `${idx + 1}\n${sub.startTime} --> ${sub.endTime}\n${sub.text}`;
    }).join('\n\n');
  };

  const handleEditStart = (subtitle) => {
    setEditingIndex(subtitle.index);
    setEditText(subtitle.text);
    setEditStartTime(subtitle.startTime);
    setEditEndTime(subtitle.endTime);
  };

  const handleEditSave = () => {
    const updated = subtitles.map(sub => {
      if (sub.index === editingIndex) {
        return {
          ...sub,
          text: editText,
          startTime: editStartTime,
          endTime: editEndTime
        };
      }
      return sub;
    });
    setSubtitles(updated);
    setEditingIndex(null);
    setHasChanges(true);
  };

  const handleEditCancel = () => {
    setEditingIndex(null);
    setEditText('');
    setEditStartTime('');
    setEditEndTime('');
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const newSrtContent = generateSRT(subtitles);
      await axios.put(`${API}/transcription/${id}/subtitles`, 
        { srt_content: newSrtContent },
        { headers: getAuthHeaders() }
      );
      setTranscription({ ...transcription, srt_content: newSrtContent });
      setHasChanges(false);
      toast.success('Subtitles saved successfully!');
    } catch (error) {
      console.error('Error saving subtitles:', error);
      toast.error('Failed to save subtitles');
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = () => {
    const srtContent = generateSRT(subtitles);
    const blob = new Blob([srtContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${transcription.filename.replace(/\.[^/.]+$/, '')}.srt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('SRT file downloaded!');
  };

  const handleCopy = () => {
    const srtContent = generateSRT(subtitles);
    navigator.clipboard.writeText(srtContent);
    toast.success('Copied to clipboard!');
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const seekToSubtitle = (subtitle) => {
    if (videoRef.current) {
      const time = parseTimestamp(subtitle.startTime);
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const isSubtitleActive = (subtitle) => {
    const start = parseTimestamp(subtitle.startTime);
    const end = parseTimestamp(subtitle.endTime);
    return currentTime >= start && currentTime <= end;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#007AFF] mx-auto animate-spin mb-4" />
          <p className="text-[#A1A1AA]">Loading transcription...</p>
        </div>
      </div>
    );
  }

  if (!transcription) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">Transcription not found</p>
          <Button onClick={() => navigate('/')} className="btn-primary">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] relative pb-20">
      <div className="hero-glow absolute top-0 left-0 right-0 h-[400px]" />
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 fade-in">
          <Button
            data-testid="back-btn"
            onClick={() => navigate('/')}
            className="btn-secondary mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <h1 className="heading-font text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white mb-1">
                Edit Subtitles
              </h1>
              <p className="text-[#A1A1AA]">{transcription.filename}</p>
            </div>
            <div className="flex items-center gap-3">
              {hasChanges && (
                <Button
                  data-testid="save-changes-btn"
                  onClick={handleSaveAll}
                  disabled={saving}
                  className="btn-primary"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Save Changes
                </Button>
              )}
              <CheckCircle className="w-10 h-10 text-[#34C759]" />
            </div>
          </div>
        </div>

        {/* Main Content - Video Preview + Editor */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Video Preview Section */}
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="bg-[#000000] p-3 border-b border-[#27272A]">
              <h2 className="heading-font text-lg font-bold text-white uppercase">Video Preview</h2>
            </div>
            <div className="relative bg-black aspect-video">
              {videoAvailable ? (
                <>
                  <video
                    ref={videoRef}
                    className="w-full h-full"
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    data-testid="video-player"
                  >
                    <source src={`${API}/transcription/${id}/video?token=${token}`} type="video/mp4" />
                  </video>
                  {/* Subtitle Overlay */}
                  {currentSubtitle && (
                    <div className="absolute bottom-12 left-0 right-0 text-center px-4">
                      <span className="bg-black/80 text-white px-4 py-2 rounded text-lg font-medium inline-block max-w-full">
                        {currentSubtitle}
                      </span>
                    </div>
                  )}
                  {/* Play/Pause Overlay Button */}
                  <button
                    onClick={handlePlayPause}
                    className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity"
                    data-testid="play-pause-overlay"
                  >
                    {isPlaying ? (
                      <Pause className="w-16 h-16 text-white/80" />
                    ) : (
                      <Play className="w-16 h-16 text-white/80" />
                    )}
                  </button>
                </>
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center text-[#52525B]">
                    <Play className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Video preview not available</p>
                    <p className="text-xs mt-1">Upload a new video to enable preview</p>
                  </div>
                </div>
              )}
            </div>
            {/* Video Controls */}
            {videoAvailable && (
              <div className="p-3 bg-[#0A0A0A] border-t border-[#27272A]">
                <div className="flex flex-col gap-2">
                  {/* Seek Bar */}
                  <div className="flex items-center gap-3">
                    <span className="text-[#A1A1AA] text-xs font-mono w-12">
                      {formatTime(currentTime)}
                    </span>
                    <input
                      type="range"
                      min="0"
                      max={transcription.duration || 100}
                      value={currentTime}
                      onChange={(e) => {
                        const time = parseFloat(e.target.value);
                        if (videoRef.current) {
                          videoRef.current.currentTime = time;
                          setCurrentTime(time);
                        }
                      }}
                      className="flex-1 h-2 bg-[#27272A] rounded-lg appearance-none cursor-pointer accent-[#007AFF]"
                      style={{
                        background: `linear-gradient(to right, #007AFF ${(currentTime / (transcription.duration || 100)) * 100}%, #27272A ${(currentTime / (transcription.duration || 100)) * 100}%)`
                      }}
                      data-testid="video-seek-bar"
                    />
                    <span className="text-[#A1A1AA] text-xs font-mono w-12 text-right">
                      {formatTime(transcription.duration || 0)}
                    </span>
                  </div>
                  {/* Play/Pause and Skip Controls */}
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.currentTime = Math.max(0, currentTime - 10);
                        }
                      }}
                      className="btn-secondary"
                      size="sm"
                      title="Rewind 10s"
                    >
                      <span className="text-xs">-10s</span>
                    </Button>
                    <Button
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.currentTime = Math.max(0, currentTime - 5);
                        }
                      }}
                      className="btn-secondary"
                      size="sm"
                      title="Rewind 5s"
                    >
                      <span className="text-xs">-5s</span>
                    </Button>
                    <Button
                      onClick={handlePlayPause}
                      className="btn-primary px-6"
                      size="sm"
                      data-testid="play-pause-btn"
                    >
                      {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </Button>
                    <Button
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.currentTime = Math.min(transcription.duration || 0, currentTime + 5);
                        }
                      }}
                      className="btn-secondary"
                      size="sm"
                      title="Forward 5s"
                    >
                      <span className="text-xs">+5s</span>
                    </Button>
                    <Button
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.currentTime = Math.min(transcription.duration || 0, currentTime + 10);
                        }
                      }}
                      className="btn-secondary"
                      size="sm"
                      title="Forward 10s"
                    >
                      <span className="text-xs">+10s</span>
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Subtitle Editor Section */}
          <div className="glass-card rounded-xl overflow-hidden">
            <div className="bg-[#000000] p-3 border-b border-[#27272A] flex items-center justify-between">
              <h2 className="heading-font text-lg font-bold text-white uppercase">Subtitle Editor</h2>
              <span className="text-[#52525B] text-sm">{subtitles.length} subtitles</span>
            </div>
            <ScrollArea className="h-[400px]" ref={subtitleListRef}>
              <div className="p-4 space-y-2">
                {subtitles.map((subtitle) => (
                  <div
                    key={subtitle.index}
                    id={`subtitle-${subtitle.index}`}
                    data-testid={`subtitle-item-${subtitle.index}`}
                    onClick={() => seekToSubtitle(subtitle)}
                    className={`rounded-lg p-3 cursor-pointer transition-all duration-200 ${
                      isSubtitleActive(subtitle)
                        ? 'bg-[#007AFF]/20 border border-[#007AFF]'
                        : 'bg-[#1A1A1A] border border-transparent hover:border-[#27272A]'
                    }`}
                  >
                    {editingIndex === subtitle.index ? (
                      // Edit Mode
                      <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-2">
                          <div className="flex-1">
                            <label className="text-[#52525B] text-xs mb-1 block">Start</label>
                            <Input
                              value={editStartTime}
                              onChange={(e) => setEditStartTime(e.target.value)}
                              className="bg-[#0A0A0A] border-[#27272A] text-white text-sm font-mono h-8"
                              placeholder="00:00:00,000"
                              data-testid="edit-start-time"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-[#52525B] text-xs mb-1 block">End</label>
                            <Input
                              value={editEndTime}
                              onChange={(e) => setEditEndTime(e.target.value)}
                              className="bg-[#0A0A0A] border-[#27272A] text-white text-sm font-mono h-8"
                              placeholder="00:00:00,000"
                              data-testid="edit-end-time"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="text-[#52525B] text-xs mb-1 block">Text</label>
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-[#27272A] text-white rounded-md p-2 text-sm resize-none"
                            rows={2}
                            data-testid="edit-text"
                          />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={handleEditSave} className="btn-primary" size="sm" data-testid="edit-save-btn">
                            Save
                          </Button>
                          <Button onClick={handleEditCancel} className="btn-secondary" size="sm" data-testid="edit-cancel-btn">
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View Mode
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-[#007AFF] text-xs font-mono">#{subtitle.index}</span>
                            <Clock className="w-3 h-3 text-[#52525B]" />
                            <span className="text-[#52525B] text-xs font-mono truncate">
                              {subtitle.startTime} → {subtitle.endTime}
                            </span>
                          </div>
                          <p className="text-white text-sm leading-relaxed whitespace-pre-wrap">{subtitle.text}</p>
                        </div>
                        <Button
                          onClick={(e) => { e.stopPropagation(); handleEditStart(subtitle); }}
                          className="btn-secondary flex-shrink-0"
                          size="sm"
                          data-testid={`edit-btn-${subtitle.index}`}
                        >
                          <Edit3 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-4 mb-6">
          <Button
            data-testid="download-srt-btn"
            onClick={handleDownload}
            className="btn-primary flex-1 sm:flex-none"
          >
            <Download className="w-5 h-5 mr-2" />
            Download SRT
          </Button>
          <Button
            data-testid="copy-srt-btn"
            onClick={handleCopy}
            className="btn-secondary flex-1 sm:flex-none"
          >
            <Copy className="w-5 h-5 mr-2" />
            Copy to Clipboard
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card rounded-xl p-4">
            <p className="text-[#A1A1AA] text-sm mb-1">Duration</p>
            <p className="heading-font text-xl font-bold text-white">
              {transcription.duration ? `${Math.floor(transcription.duration / 60)}:${String(Math.floor(transcription.duration % 60)).padStart(2, '0')}` : 'N/A'}
            </p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <p className="text-[#A1A1AA] text-sm mb-1">Subtitles</p>
            <p className="heading-font text-xl font-bold text-white">{subtitles.length}</p>
          </div>
          <div className="glass-card rounded-xl p-4">
            <p className="text-[#A1A1AA] text-sm mb-1">Language</p>
            <p className="heading-font text-xl font-bold text-white uppercase">{transcription.language || 'Mixed'}</p>
          </div>
        </div>
      </div>
      <BrandingFooter />
    </div>
  );
}

function formatTime(seconds) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}
